import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { CertificateHideableField } from '@repo/types'
import { productApi } from 'services'
import { useSnackbar } from 'contexts'
import { getErrorMessage } from 'utils'

/**
 * VRG-788 — replace the set of STANDARD certificate fields this SKU hides.
 *
 * The error path matters more here than for most mutations: the API REFUSES an unknown
 * or locked key rather than normalizing it away, so a 400 carries a sentence naming what
 * cannot be hidden. Surfacing `getErrorMessage` rather than a generic fallback is what
 * makes that sentence reach the brand.
 */
export const useProductCertificateFields = () => {
  const queryClient = useQueryClient()
  const { open: notification } = useSnackbar()

  return useMutation({
    mutationFn: ({
      productId,
      hiddenFields,
    }: {
      productId: string
      hiddenFields: CertificateHideableField[]
    }) => productApi.setCertificateFields(productId, hiddenFields),
    onSuccess: (product) => {
      queryClient.invalidateQueries({ queryKey: ['product', product.id] })
      queryClient.invalidateQueries({ queryKey: ['products'] })
      notification({ type: 'success', title: 'Certificate visibility saved' })
    },
    onError: (error: any) => {
      notification({
        type: 'error',
        title: 'Could not save certificate fields',
        text: getErrorMessage(error) || 'Failed to update certificate fields',
      })
    },
  })
}
